import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { codigoValidacaoService } from '../../services/codigoValidacaoService';
import { CodigoValidacao } from '../../types/codigoValidacao';
import { TerritoryDetailsModal } from '../../components/TerritoryDetailsModal';

interface RegiaoParaModal {
    municipio: { id: number; nome: string; nome_original: string; };
    bairros: { id: number; nome_original: string; }[];
}

export const DetalhesPedido: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [codigo, setCodigo] = useState<CodigoValidacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [revokeInput, setRevokeInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [territoryModalState, setTerritoryModalState] = useState<{ isOpen: boolean; regiao: RegiaoParaModal | null }>({ isOpen: false, regiao: null });

  useEffect(() => {
    const fetchCodigo = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await codigoValidacaoService.getCodigoPorId(id);
        setCodigo(response);
      } catch (err) {
        console.error('Erro ao carregar detalhes do código de validação:', err);
        setError('Não foi possível carregar os detalhes do pedido.');
      } finally {
        setLoading(false);
      }
    };

    fetchCodigo();
  }, [id]);

  const handleRevogar = async () => {
    if (!id) return;

    try {
      const codigoRevogado = await codigoValidacaoService.revogar(id);
      setCodigo(codigoRevogado);
      setIsModalOpen(false);
      setRevokeInput('');
    } catch (err: any) {
      console.error('Erro ao revogar código:', err);
      setError(err.response?.data?.detail || 'Não foi possível revogar o código.');
      setIsModalOpen(false);
      setRevokeInput('');
    }
  };

  const getStatusBadge = (status: CodigoValidacao['status']) => {
    switch (status) {
      case 'PENDENTE':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Pendente</span>;
      case 'UTILIZADO':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Utilizado</span>;
      case 'EXPIRADO':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Expirado</span>;
      case 'REVOGADO':
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-400 text-white">Revogado</span>;
      default:
        return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32bef0]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <h3 className="text-sm font-medium text-red-800">{error}</h3>
      </div>
    );
  }

  if (!codigo) {
    return <div>Pedido não encontrado.</div>;
  }

  return (
    <>
      <TerritoryDetailsModal 
        isOpen={territoryModalState.isOpen}
        onClose={() => setTerritoryModalState({ isOpen: false, regiao: null })}
        municipio={territoryModalState.regiao?.municipio || null}
        includedBairros={territoryModalState.regiao?.bairros || []}
      />

      <div className="max-w-7xl mx-auto">
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-[#29306a]">Detalhes do Pedido</h1>
            <p className="mt-2 text-sm text-[#64748b]">
              Informações detalhadas sobre o código de convite.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex items-center gap-x-4">
            <Link to="/franquias/pedidos" className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                Voltar
            </Link>
            {codigo.status === 'PENDENTE' && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Revogar
              </button>
            )}
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Informações do Código</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Email Convidado</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{codigo.email_convidado}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{getStatusBadge(codigo.status)}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Território Proposto</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {codigo.territorio_proposto && (
                    <div>
                      {(codigo.territorio_proposto.municipios_completos?.length || 0) > 0 && (
                        <div>
                          <h4 className="font-semibold">Cidades Completas:</h4>
                          <ul className="list-disc pl-5">
                            {codigo.territorio_proposto.municipios_completos.map(c => (
                              c && <li key={c.id}>{c.nome_original}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(codigo.territorio_proposto.regiao_municipio?.length || 0) > 0 && (
                        <div className="mt-2">
                          <h4 className="font-semibold">Cidades Parciais (Bairros):</h4>
                          <ul className="divide-y divide-gray-200">
                            {codigo.territorio_proposto.regiao_municipio.map(regiao => (
                                regiao && <li key={regiao.municipio?.id} className="py-3 flex justify-between items-center">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{regiao.municipio?.nome_original}</p>
                                        <p className="text-sm text-gray-500">{regiao.bairros?.length || 0} bairros selecionados</p>
                                    </div>
                                    <button 
                                        onClick={() => setTerritoryModalState({ isOpen: true, regiao: regiao })}
                                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                    >
                                        Ver Detalhes
                                    </button>
                                </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Criado Em</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{new Date(codigo.criado_em).toLocaleString('pt-BR')}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Expira Em</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{new Date(codigo.expira_em).toLocaleString('pt-BR')}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Utilizado Em</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{codigo.utilizado_em ? new Date(codigo.utilizado_em).toLocaleString('pt-BR') : '-'}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Franquia Associada</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{codigo.franquia || '-'}</dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Link do Convite</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <input 
                      type="text" 
                      readOnly 
                      value={`${window.location.origin}/register?codigo=${codigo.id}`} 
                      className="w-full bg-gray-100 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/register?codigo=${codigo.id}`);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="ml-3 inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                    >
                      {copied ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-10" aria-hidden="true">
          <div className="flex items-center justify-center min-h-screen">
            <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-sm w-full">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Confirmar Revogação</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Esta ação não pode ser desfeita. Para confirmar, digite <strong>REVOGAR</strong> no campo abaixo.
                </p>
              </div>
              <div className="mt-4">
                <input 
                  type="text"
                  value={revokeInput}
                  onChange={(e) => setRevokeInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleRevogar}
                  disabled={revokeInput !== 'REVOGAR'}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Revogar
                </button>
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); setRevokeInput(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};